import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  busy?: boolean;
  children: ReactNode;
  footer: ReactNode;
}

/**
 * Stage 12 (A6): minimal modal primitive used by the three create-new
 * dialogs (images / music / speech). Reuses the existing .modal-backdrop
 * and .modal CSS classes from SettingsModal so styling is consistent.
 */
export function NewAssetModal({
  open,
  title,
  onClose,
  busy,
  children,
  footer,
}: Props): JSX.Element | null {
  const firstFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      const el = firstFieldRef.current?.querySelector<HTMLElement>(
        'textarea, input, select',
      );
      el?.focus();
    }, 0);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="modal new-asset-modal">
        <header className="modal-header">
          <h2>{title}</h2>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <section className="modal-body" ref={firstFieldRef}>
          {children}
        </section>
        <footer className="modal-footer new-asset-footer">{footer}</footer>
      </div>
    </div>
  );
}
